// CRM Opportunities — flexible multi-pipeline kanban board. Pipelines and stages
// are user-editable data (HubSpot/Pipedrive model): any number of pipelines per
// client, stages carry colour, win probability, type (open/won/lost) and an
// optional rotten-days threshold. Moving a card into a won or lost stage closes
// the opportunity. Weighted value = value x stage probability.
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import toast from 'react-hot-toast';
import API_BASE from '../../config';
import { DealDrawer } from './DealDrawer';

const PALETTE = ['#64748B', '#2E8FFF', '#F5A623', '#7C5CFC', '#00A878', '#F43F5E', '#06B6D4', '#EC4899', '#FF7A59'];

const gbp = (n) => '\u00A3' + Number(n || 0).toLocaleString('en-GB', { maximumFractionDigits: 0 });

function daysIn(since) {
  if (!since) return 0;
  return Math.floor((Date.now() - new Date(since).getTime()) / 86400000);
}

function fmtDate(v) {
  if (!v) return '';
  const d = new Date(v);
  if (isNaN(d)) return '';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

// ---------- Pipeline editor modal (create + edit) ----------
const DEFAULT_ROWS = [
  { name: 'New', colour: '#64748B', win_probability: 10, stage_type: 'open', rotten_days: '' },
  { name: 'Discovery', colour: '#2E8FFF', win_probability: 25, stage_type: 'open', rotten_days: '' },
  { name: 'Proposal', colour: '#F5A623', win_probability: 50, stage_type: 'open', rotten_days: '' },
  { name: 'Negotiation', colour: '#7C5CFC', win_probability: 75, stage_type: 'open', rotten_days: '' },
  { name: 'Won', colour: '#00A878', win_probability: 100, stage_type: 'won', rotten_days: '' },
  { name: 'Lost', colour: '#F43F5E', win_probability: 0, stage_type: 'lost', rotten_days: '' }
];

function PipelineModal({ pipeline, onClose, onSaved, clientId }) {
  const isEdit = !!pipeline;
  const [name, setName] = useState(pipeline ? pipeline.name : '');
  const [rows, setRows] = useState(
    pipeline
      ? pipeline.stages.map(s => ({ id: s.id, name: s.name, colour: s.colour, win_probability: s.win_probability, stage_type: s.stage_type, rotten_days: s.rotten_days == null ? '' : String(s.rotten_days), opp_count: s.opp_count || 0 }))
      : DEFAULT_ROWS.map(r => ({ ...r }))
  );
  const [removed, setRemoved] = useState([]);
  const [saving, setSaving] = useState(false);

  const setRow = (idx, patch) => setRows(rs => rs.map((r, i) => i === idx ? { ...r, ...patch } : r));
  const move = (idx, dir) => setRows(rs => {
    const j = idx + dir;
    if (j < 0 || j >= rs.length) return rs;
    const copy = rs.slice();
    const t = copy[idx]; copy[idx] = copy[j]; copy[j] = t;
    return copy;
  });
  const removeRow = (idx) => {
    const row = rows[idx];
    if (row.id && row.opp_count > 0) { toast.error('That stage still holds opportunities. Move them first.'); return; }
    if (row.id) setRemoved(rm => [...rm, row.id]);
    setRows(rs => rs.filter((_, i) => i !== idx));
  };
  const addRow = () => setRows(rs => [...rs, { name: '', colour: '#64748B', win_probability: 50, stage_type: 'open', rotten_days: '' }]);

  const save = async () => {
    if (!name.trim()) { toast.error('Give the pipeline a name'); return; }
    if (!rows.length) { toast.error('A pipeline needs at least one stage'); return; }
    if (rows.some(r => !r.name.trim())) { toast.error('Every stage needs a name'); return; }
    setSaving(true);
    try {
      if (!isEdit) {
        const r = await fetch(`${API_BASE}/clients/${clientId}/pipelines`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: name.trim(),
            stages: rows.map(s => ({ name: s.name.trim(), colour: s.colour, win_probability: parseInt(s.win_probability) || 0, stage_type: s.stage_type, rotten_days: s.rotten_days === '' ? null : parseInt(s.rotten_days) }))
          })
        });
        if (!r.ok) throw new Error((await r.json()).error || 'Create failed');
        toast.success('Pipeline created');
      } else {
        if (name.trim() !== pipeline.name) {
          const r = await fetch(`${API_BASE}/pipelines/${pipeline.id}`, {
            method: 'PATCH', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name: name.trim() })
          });
          if (!r.ok) throw new Error('Rename failed');
        }
        for (const id of removed) {
          const r = await fetch(`${API_BASE}/stages/${id}`, { method: 'DELETE' });
          if (!r.ok) { const j = await r.json(); throw new Error(j.error || 'Stage delete failed'); }
        }
        const finalIds = [];
        for (const row of rows) {
          if (row.id) {
            const orig = pipeline.stages.find(s => s.id === row.id);
            const rd = row.rotten_days === '' ? null : parseInt(row.rotten_days);
            if (orig && (orig.name !== row.name.trim() || orig.colour !== row.colour || orig.win_probability !== (parseInt(row.win_probability) || 0) || orig.stage_type !== row.stage_type || (orig.rotten_days == null ? null : orig.rotten_days) !== rd)) {
              const r = await fetch(`${API_BASE}/stages/${row.id}`, {
                method: 'PATCH', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name: row.name.trim(), colour: row.colour, win_probability: parseInt(row.win_probability) || 0, stage_type: row.stage_type, rotten_days: rd })
              });
              if (!r.ok) throw new Error('Stage update failed');
            }
            finalIds.push(row.id);
          } else {
            const r = await fetch(`${API_BASE}/pipelines/${pipeline.id}/stages`, {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ name: row.name.trim(), colour: row.colour, win_probability: parseInt(row.win_probability) || 0, stage_type: row.stage_type, rotten_days: row.rotten_days === '' ? null : parseInt(row.rotten_days) })
            });
            if (!r.ok) throw new Error('Stage create failed');
            const created = await r.json();
            finalIds.push(created.id);
          }
        }
        const r2 = await fetch(`${API_BASE}/pipelines/${pipeline.id}/stages/reorder`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ stage_ids: finalIds })
        });
        if (!r2.ok) throw new Error('Reorder failed');
        toast.success('Pipeline updated');
      }
      onSaved();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="crmop-overlay" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="crmop-modal crmop-modal--wide">
        <div className="crmop-modal-h">
          <h3>{isEdit ? 'Edit pipeline' : 'New pipeline'}</h3>
          <button className="crmop-x" onClick={onClose}>{'\u00D7'}</button>
        </div>
        <div className="crmop-modal-b">
          <label className="crmop-lbl">Pipeline name</label>
          <input className="crmop-inp" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Sales pipeline, Renewals, Partnerships" />

          <div className="crmop-stg-head">
            <span>Stages</span>
            <span className="crmop-stg-hint">Order is left to right on the board. Probability drives the weighted total.</span>
          </div>

          <div className="crmop-stg-rows">
            {rows.map((row, idx) => (
              <div key={row.id || 'new-' + idx} className="crmop-stg-row">
                <div className="crmop-stg-order">
                  <button className="crmop-stg-arrow" disabled={idx === 0} onClick={() => move(idx, -1)}>{'\u25B2'}</button>
                  <button className="crmop-stg-arrow" disabled={idx === rows.length - 1} onClick={() => move(idx, 1)}>{'\u25BC'}</button>
                </div>
                <input className="crmop-inp crmop-stg-name" value={row.name} placeholder="Stage name" onChange={e => setRow(idx, { name: e.target.value })} />
                <div className="crmop-swatches">
                  {PALETTE.map(c => (
                    <button key={c} className={'crmop-swatch' + (row.colour === c ? ' crmop-swatch--sel' : '')} style={{ background: c }} onClick={() => setRow(idx, { colour: c })} title={c} />
                  ))}
                </div>
                <div className="crmop-stg-prob">
                  <input className="crmop-inp crmop-inp-num" type="number" min="0" max="100" value={row.win_probability} onChange={e => setRow(idx, { win_probability: e.target.value })} />
                  <span className="crmop-stg-pc">%</span>
                </div>
                <select className="crmop-inp crmop-stg-type" value={row.stage_type} onChange={e => setRow(idx, { stage_type: e.target.value })}>
                  <option value="open">Open</option>
                  <option value="won">Won</option>
                  <option value="lost">Lost</option>
                </select>
                <div className="crmop-stg-rot">
                  <input className="crmop-inp crmop-inp-num" type="number" min="1" placeholder="—" value={row.rotten_days} onChange={e => setRow(idx, { rotten_days: e.target.value })} title="Days before an idle opportunity in this stage is flagged stale" />
                  <span className="crmop-stg-pc">d</span>
                </div>
                <button className="crmop-stg-del" onClick={() => removeRow(idx)} title="Remove stage">{'\u00D7'}</button>
              </div>
            ))}
          </div>
          <button className="crmop-btn-ghost" onClick={addRow}>+ Add stage</button>
        </div>
        <div className="crmop-modal-f">
          <button className="crmop-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="crmop-btn" onClick={save} disabled={saving}>{saving ? 'Saving\u2026' : isEdit ? 'Save changes' : 'Create pipeline'}</button>
        </div>
      </div>
    </div>
  );
}

// ---------- Opportunity modal (create + edit) ----------
// ---------- Main screen ----------
export default function CrmOpportunities() {
  const { clientId } = useOutletContext();
  const [pipelines, setPipelines] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [opps, setOpps] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pipeModal, setPipeModal] = useState(null); // 'new' | pipeline object
  const [oppModal, setOppModal] = useState(null);   // { opp?, defaultStageId? }
  const [dragOverStage, setDragOverStage] = useState(null);
  const dragId = useRef(null);

  const loadPipelines = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/clients/${clientId}/pipelines`);
      const data = await r.json();
      setPipelines(data);
      setActiveId(cur => (cur && data.some(p => p.id === cur)) ? cur : (data[0] ? data[0].id : null));
    } catch (e) {
      toast.error('Failed to load pipelines');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  const loadOpps = useCallback(async () => {
    if (!activeId) { setOpps([]); return; }
    try {
      const r = await fetch(`${API_BASE}/clients/${clientId}/opportunities?pipeline_id=${activeId}`);
      setOpps(await r.json());
    } catch (e) {
      toast.error('Failed to load opportunities');
    }
  }, [clientId, activeId]);

  useEffect(() => { loadPipelines(); }, [loadPipelines]);
  useEffect(() => { loadOpps(); }, [loadOpps]);
  useEffect(() => {
    (async () => {
      try {
        const [rc, rk] = await Promise.all([
          fetch(`${API_BASE}/clients/${clientId}/companies`),
          fetch(`${API_BASE}/clients/${clientId}/contacts`)
        ]);
        const co = await rc.json();
        const ct = await rk.json();
        setCompanies(Array.isArray(co) ? co : (co.companies || []));
        setContacts(Array.isArray(ct) ? ct : (ct.contacts || []));
      } catch (e) { /* pickers degrade gracefully */ }
    })();
  }, [clientId]);

  const active = pipelines.find(p => p.id === activeId) || null;

  const byStage = useMemo(() => {
    const m = {};
    for (const o of opps) (m[o.stage_id] = m[o.stage_id] || []).push(o);
    return m;
  }, [opps]);

  const totals = useMemo(() => {
    if (!active) return { count: 0, weighted: 0, open: 0 };
    let weighted = 0, open = 0;
    for (const o of opps) {
      const v = parseFloat(o.value) || 0;
      weighted += v * (o.win_probability || 0) / 100;
      if (o.status === 'open') open += v;
    }
    return { count: opps.length, weighted, open };
  }, [opps, active]);

  const onDrop = async (stageId) => {
    setDragOverStage(null);
    const id = dragId.current;
    dragId.current = null;
    if (!id) return;
    const opp = opps.find(o => o.id === id);
    if (!opp || opp.stage_id === stageId) return;
    const prev = opps;
    const stage = active.stages.find(s => s.id === stageId);
    setOpps(os => os.map(o => o.id === id ? { ...o, stage_id: stageId, stage_entered_at: new Date().toISOString(), win_probability: stage ? stage.win_probability : o.win_probability, status: stage ? (stage.stage_type === 'won' ? 'won' : stage.stage_type === 'lost' ? 'lost' : 'open') : o.status } : o));
    try {
      const r = await fetch(`${API_BASE}/opportunities/${id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stage_id: stageId })
      });
      if (!r.ok) throw new Error((await r.json()).error || 'Move failed');
      loadOpps();
    } catch (e) {
      toast.error(e.message);
      setOpps(prev);
    }
  };

  const deletePipeline = async () => {
    if (!active) return;
    const n = opps.length;
    if (n > 0) { toast.error(`This pipeline still holds ${n} opportunities. Move or delete them first.`); return; }
    if (!window.confirm(`Delete pipeline "${active.name}"?`)) return;
    try {
      const r = await fetch(`${API_BASE}/pipelines/${active.id}`, { method: 'DELETE' });
      if (!r.ok) throw new Error((await r.json()).error || 'Delete failed');
      toast.success('Pipeline deleted');
      setActiveId(null);
      loadPipelines();
    } catch (e) {
      toast.error(e.message);
    }
  };

  if (loading) return <div className="crmop-load">Loading opportunities{'\u2026'}</div>;

  // Empty state: no pipelines yet
  if (!pipelines.length) {
    return (
      <div className="crmop-wrap">
        <div className="crmop-empty-hero">
          <div className="crmop-empty-ico">{'\u25C7'}</div>
          <h2>Track your revenue in pipelines you design</h2>
          <p>Create a pipeline, shape its stages to match how you actually sell, and drag opportunities across the board. You can run as many pipelines as you need, each with its own stages, colours and win probabilities.</p>
          <button className="crmop-btn" onClick={() => setPipeModal('new')}>Create your first pipeline</button>
        </div>
        {pipeModal && (
          <PipelineModal pipeline={pipeModal === 'new' ? null : pipeModal} clientId={clientId}
            onClose={() => setPipeModal(null)}
            onSaved={() => { setPipeModal(null); loadPipelines(); }} />
        )}
      </div>
    );
  }

  return (
    <div className="crmop-wrap">
      <div className="crmop-top">
        <div className="crmop-title-blk">
          <h2>Opportunities</h2>
          {active && (
            <span className="crmop-tot">
              <b>{totals.count} {totals.count === 1 ? 'opportunity' : 'opportunities'}</b>
              {' \u00B7 '}{gbp(totals.open)} open{' \u00B7 '}{gbp(totals.weighted)} weighted
            </span>
          )}
        </div>
        <div className="crmop-top-actions">
          <select className="crmop-pipe-sel" value={activeId || ''} onChange={e => setActiveId(parseInt(e.target.value))}>
            {pipelines.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          {active && <button className="crmop-btn-ghost" onClick={() => setPipeModal(active)} title="Edit stages, colours, probabilities">Edit pipeline</button>}
          {active && <button className="crmop-btn-ghost crmop-btn-ghost--danger" onClick={deletePipeline} title="Delete this pipeline">Delete</button>}
          <button className="crmop-btn-ghost" onClick={() => setPipeModal('new')}>+ New pipeline</button>
          {active && <button className="crmop-btn" onClick={() => setOppModal({})}>+ New opportunity</button>}
        </div>
      </div>

      {active && (
        <div className="crmop-board">
          {active.stages.map(stage => {
            const list = byStage[stage.id] || [];
            const sum = list.reduce((a, o) => a + (parseFloat(o.value) || 0), 0);
            return (
              <div key={stage.id}
                   className={'crmop-col' + (dragOverStage === stage.id ? ' crmop-col--over' : '')}
                   onDragOver={e => { e.preventDefault(); setDragOverStage(stage.id); }}
                   onDragLeave={() => setDragOverStage(s => s === stage.id ? null : s)}
                   onDrop={() => onDrop(stage.id)}>
                <div className="crmop-col-h">
                  <span className="crmop-col-dot" style={{ background: stage.colour }} />
                  <span className="crmop-col-nm" style={{ color: stage.colour }}>{stage.name}</span>
                  {stage.stage_type !== 'open' && <span className="crmop-col-type">{stage.stage_type}</span>}
                  <span className="crmop-col-ct">{list.length}</span>
                </div>
                <div className="crmop-col-sum">{gbp(sum)}{stage.win_probability < 100 && stage.stage_type === 'open' ? ` \u00B7 ${stage.win_probability}%` : ''}</div>
                <div className="crmop-col-cards">
                  {list.map(o => {
                    const stale = stage.rotten_days && o.status === 'open' && daysIn(o.stage_entered_at) >= stage.rotten_days;
                    return (
                      <div key={o.id} draggable
                           className={'crmop-card' + (stale ? ' crmop-card--stale' : '')}
                           style={{ borderLeftColor: stage.colour }}
                           onDragStart={() => { dragId.current = o.id; }}
                           onClick={() => setOppModal({ opp: o })}>
                        {o.company_name && <div className="crmop-card-co">{o.company_name}</div>}
                        <div className="crmop-card-nm">{o.name}</div>
                        <div className="crmop-card-meta">
                          <span className="crmop-card-val">{gbp(o.value)}</span>
                          {o.contact_name && <span className="crmop-card-ct">{o.contact_name}</span>}
                          {stale && <span className="crmop-card-stale" title={`Idle ${daysIn(o.stage_entered_at)} days in this stage`}>{daysIn(o.stage_entered_at)}d idle</span>}
                        </div>
                        {o.expected_close_date && <div className="crmop-card-close">Close {fmtDate(o.expected_close_date)}</div>}
                      </div>
                    );
                  })}
                  {!list.length && <div className="crmop-col-empty">Drop opportunities here</div>}
                </div>
                <button className="crmop-col-add" onClick={() => setOppModal({ defaultStageId: stage.id })}>+ Add</button>
              </div>
            );
          })}
        </div>
      )}

      {pipeModal && (
        <PipelineModal pipeline={pipeModal === 'new' ? null : pipeModal} clientId={clientId}
          onClose={() => setPipeModal(null)}
          onSaved={() => { setPipeModal(null); loadPipelines(); loadOpps(); }} />
      )}
      {oppModal && active && (
        <DealDrawer opp={oppModal.opp} pipeline={active} defaultStageId={oppModal.defaultStageId}
          companies={companies} contacts={contacts} clientId={clientId}
          onClose={() => setOppModal(null)}
          onSaved={() => { setOppModal(null); loadOpps(); loadPipelines(); }} />
      )}
    </div>
  );
}
